import { chunk, compact, startCase, uniq } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { buildPatientSearchQuery } from 'app/components/contacts/drawer/PmsIntegration'
import { useDrawerContact } from 'app/hooks/useDrawerContact'
import { useLocationFilter } from 'app/hooks/useLocationFilter'
import type { Provider } from 'app/generated/graphql'
import type { ContactStore } from 'app/stores/ContactStore'
import type { LocationStore } from 'app/stores/LocationStore'
import type { PatientStore } from 'app/stores/PatientStore'
import {
  useAppointmentStore,
  useContactStore,
  useLocationStore,
  usePatientStore,
  useProviderStore,
} from 'app/stores/StoreContext'
import type { LocationConsumer } from 'app/types/provider-directory'
import { useHandleSearchParams } from 'app/utils'
import type {
  CalendarAppointment,
  CalendarPatient,
  CalendarView,
  ColumnGrouping,
  ProviderInfo,
  SchedulingRoomsRead,
} from 'app/types/appointment-calendar'
import {
  CALLED_OFF_TONES,
  calendarDay,
  calendarRange,
  calendarSearch,
  calendarView,
  dayCounts,
  providerColumns,
  providerFullName,
  roomColumns,
  roomLookup,
  toVisits,
} from 'lib/appointmentCalendar'
import { contactsForPatients, patientLinks } from 'lib/appointmentsPage'
import { bg } from 'lib/background'
import { log, logError } from 'lib/logging'

const PROVIDER_LIMIT = 500
const LOCATION_LIMIT = 500
const PATIENT_BATCH = 100
// One patient is sometimes carried by more than one contact (a merge left behind); read room for a few.
const CONTACTS_PER_PATIENT = 3

type CalendarProvider = Pick<Provider, `vendorId` | `kollaConsumerId` | `displayName` | `pmsDisplayName` | `taxonomy`>

type Read = {
  key: string
  appointments: CalendarAppointment[]
  failed: boolean
  /** Undefined off the day view, and when any location's rooms could not be read. */
  rooms?: SchedulingRoomsRead[]
}

/** All or nothing, so a location that failed never reads as a column of roomless visits. */
async function readRooms(
  locations: LocationStore,
  locationIds: string[],
  day: string,
): Promise<SchedulingRoomsRead[] | undefined> {
  try {
    return await Promise.all(locationIds.map((locationId) => locations.readRooms(locationId, day)))
  } catch (err) {
    logError(`Error reading calendar rooms`, err)
    return undefined
  }
}

async function readPatients(
  stores: { patients: PatientStore; contacts: ContactStore },
  patientIds: string[],
): Promise<Map<string, CalendarPatient>> {
  const batches = await Promise.all(
    chunk(patientIds, PATIENT_BATCH).map(async (ids) => {
      const [patients, contacts] = await Promise.all([
        stores.patients.search({ search: buildPatientSearchQuery(ids), pagination: { limit: ids.length } }),
        stores.contacts.search({
          search: contactsForPatients(ids),
          pagination: { limit: ids.length * CONTACTS_PER_PATIENT },
        }),
      ])
      if (patients.error || contacts.error) throw new Error((patients.error ?? contacts.error)?.message)
      const links = patientLinks(contacts.hits, ids)
      const names = new Map(
        patients.hits.map((patient) => [
          patient.id,
          startCase(compact([patient.firstName, patient.lastName]).join(` `).toLowerCase()),
        ]),
      )
      return ids.map((id): [string, CalendarPatient] => {
        const link = links.get(id)
        return [id, { name: link?.name || names.get(id) || ``, contactId: link?.contactId }]
      })
    }),
  )
  return new Map(batches.flat())
}

export function useAppointmentsCalendar() {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  // One clock per visit: a page left open past midnight keeps its day until it is reloaded.
  const now = useMemo(() => new Date(), [])
  const view = calendarView(searchParams.get(`view`))
  const day = calendarDay(searchParams.get(`day`), now)
  const range = useMemo(() => calendarRange(view, day), [view, day])
  const location = useLocationFilter()
  const search = calendarSearch(range, location.locationIds)
  const appointmentStore = useAppointmentStore()
  const locationStore = useLocationStore()
  const providerStore = useProviderStore()
  const patientStore = usePatientStore()
  const contactStore = useContactStore()
  const [showCalledOff, setShowCalledOff] = useState(false)
  const [query, setQuery] = useState(``)

  const [providers, setProviders] = useState<{ list: CalendarProvider[]; locations: LocationConsumer[] }>({
    list: [],
    locations: [],
  })
  useEffect(() => {
    bg(
      Promise.all([
        providerStore.search({ pagination: { limit: PROVIDER_LIMIT } }),
        locationStore.search({ pagination: { limit: LOCATION_LIMIT } }),
      ])
        .then(([providerList, locationList]) => {
          if (providerList.error || locationList.error) {
            throw new Error((providerList.error ?? locationList.error)?.message)
          }
          if (providerList.hits.length >= PROVIDER_LIMIT) {
            log(`useAppointmentsCalendar: provider list hit the ${PROVIDER_LIMIT} cap; some names may be missing`)
          }
          setProviders({ list: providerList.hits, locations: locationList.hits })
        })
        .catch((err) => logError(`Error reading providers for the calendar`, err)),
    )
  }, [])
  // A vendorId such as `provider_3` repeats across a workspace's locations, so it is keyed by the location's PMS consumer too.
  const describe = useMemo(() => {
    const consumerByLocation = new Map(providers.locations.map((loc) => [loc.id, loc.kollaConsumerId]))
    const byKey = new Map(
      providers.list.map((provider) => [`${provider.kollaConsumerId}:${provider.vendorId}`, provider]),
    )
    return (locationId: string | null | undefined, providerId: string): ProviderInfo | undefined => {
      const provider = byKey.get(`${consumerByLocation.get(locationId ?? ``)}:${providerId}`)
      if (!provider) return undefined
      const hygienist = provider.taxonomy === `Hygienist`
      return {
        name: providerFullName(provider.pmsDisplayName) || provider.displayName || providerId,
        role: hygienist ? `Hygienist` : provider.taxonomy === `General` ? `Dentist` : provider.taxonomy || undefined,
        hygienist,
      }
    }
  }, [providers])

  const [read, setRead] = useState<Read>()
  const [reloads, setReloads] = useState(0)
  const roomDay = view === `day` ? day : undefined
  useEffect(() => {
    let current = true
    bg(
      appointmentStore
        .readAll({ search, sort: { field: `date,time`, direction: `ASC` } })
        .then(async (appointments) => {
          const locationIds = uniq(compact(appointments.map((appointment) => appointment.locationId)))
          // Read with the appointments, so a day never draws in one layout and then jumps to the other.
          const rooms = roomDay && locationIds.length ? await readRooms(locationStore, locationIds, roomDay) : undefined
          if (current) setRead({ key: search, appointments, failed: false, rooms })
          // The next and previous days are the likeliest clicks; their rooms are read while this one is looked at.
          if (rooms && roomDay) {
            for (const step of [1, -1]) {
              bg(readRooms(locationStore, locationIds, moment(roomDay).add(step, `day`).format(`YYYY-MM-DD`)))
            }
          }
        })
        .catch((err) => {
          logError(`Error reading the appointments calendar`, err)
          if (current) setRead({ key: search, appointments: [], failed: true })
        }),
    )
    return () => {
      current = false
    }
  }, [search, roomDay, reloads])
  // The last range stays on screen while the next one loads, so navigating never flashes an empty board.
  const loading = read?.key !== search
  const appointments = read?.appointments ?? []

  const [patients, setPatients] = useState<ReadonlyMap<string, CalendarPatient>>(new Map())
  // A month draws counts only, so it never asks who the patients are.
  const unresolvedKey =
    view === `month`
      ? ``
      : uniq(compact(appointments.map((appointment) => appointment.patientId)))
          .filter((patientId) => !patients.has(patientId))
          .join(`,`)
  useEffect(() => {
    if (!unresolvedKey) return
    bg(
      readPatients({ patients: patientStore, contacts: contactStore }, unresolvedKey.split(`,`))
        .then((resolved) => setPatients((prev) => new Map([...prev, ...resolved])))
        .catch((err) => logError(`Error reading calendar patients`, err)),
    )
  }, [unresolvedKey])

  const allVisits = useMemo(
    () =>
      toVisits(appointments, {
        now,
        isHygienist: (locationId, providerId) => describe(locationId, providerId)?.hygienist ?? false,
      }),
    [appointments, describe],
  )
  // A month's grid runs to whole weeks; its total counts only the month's own days.
  const counted = view === `month` ? allVisits.filter((visit) => visit.day.slice(0, 7) === day.slice(0, 7)) : allVisits
  const calledOffCount = counted.filter((visit) => CALLED_OFF_TONES.has(visit.tone)).length
  const needle = query.trim().toLowerCase()
  const visits = allVisits.filter(
    (visit) =>
      (showCalledOff || !CALLED_OFF_TONES.has(visit.tone)) &&
      (!needle || (patients.get(visit.appointment.patientId ?? ``)?.name ?? ``).toLowerCase().includes(needle)),
  )
  const grouping: ColumnGrouping = searchParams.get(`columns`) === `provider` ? `provider` : `room`
  const rooms = read?.rooms
  const roomOf = useMemo(() => (rooms ? roomLookup(rooms) : undefined), [rooms])
  const columns =
    view !== `day`
      ? []
      : grouping === `room` && roomOf
        ? roomColumns(visits, roomOf, describe)
        : providerColumns(visits, describe)
  const counts = useMemo(() => dayCounts(allVisits), [allVisits])

  const drawer = useDrawerContact()

  return {
    view,
    day,
    today: calendarDay(null, now),
    now,
    range,
    loading,
    firstLoad: read === undefined,
    failed: !loading && !!read?.failed,
    retry: () => setReloads((count) => count + 1),
    visits,
    columns,
    grouping,
    // Rooms were asked for and could not be read, so the board fell back to providers.
    roomsMissing: grouping === `room` && view === `day` && !loading && !!read?.appointments.length && !rooms,
    setGrouping: (next: ColumnGrouping) => handleSearchParams({ columns: next === `room` ? undefined : next }),
    counts,
    bookedCount: counted.length - calledOffCount,
    calledOffCount,
    showCalledOff,
    setShowCalledOff,
    query,
    setQuery,
    patientFor: (patientId: string | null | undefined) => (patientId ? patients.get(patientId) : undefined),
    describe,
    location,
    setView: (next: CalendarView) => handleSearchParams({ view: next }),
    goTo: (next: string, nextView?: CalendarView) => handleSearchParams({ day: next, view: nextView ?? view }),
    step: (direction: 1 | -1) => handleSearchParams({ day: moment(day).add(direction, view).format(`YYYY-MM-DD`) }),
    openContact: (contactId: string) => handleSearchParams({ drawer: contactId }),
    drawer,
  }
}

export type AppointmentsCalendarState = ReturnType<typeof useAppointmentsCalendar>
